import { formatDay, formatMonth, type DayKey, type MonthKey, type SphereId } from '@superposition/domain';
import type { Messages } from './index.js';

/** «Katya's», «James'» */
function possessive(name: string): string {
  return /s$/i.test(name) ? `${name}'` : `${name}'s`;
}

/** «1 day», «2 days» */
function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/**
 * английские тексты приложения: строчными, как и русские (docs/en-voice-contract.md).
 * форму задает русский словарь.
 */
export const en: Messages = {
  common: {
    brand: 'superposition',
    documentTitle: 'superposition',
    close: 'close',
    cancel: 'cancel',
    save: 'save',
    saving: 'saving…',
    saved: 'saved',
    formatDay: (day: DayKey): string => formatDay(day, 'en'),
    formatMonth: (month: MonthKey): string => formatMonth(month, 'en'),
  },

  app: {
    nav: {
      wheel: 'wheel',
      matrix: 'matrix',
      obs: 'observer',
      lake: 'lake',
      method: 'method',
    },
    settingsAria: 'settings',
    checkingSession: 'checking your telegram session…',
    noTelegram: 'this is a telegram mini app. open it inside telegram, from the @superposition_kate_bot menu.',
    noTelegramBrowser: "you can't sign in from a regular browser: only telegram can confirm it's you.",
    accessExpiredTitle: 'your access has expired',
    accessInviteTitle: 'invite only',
    accessExpiredText: 'your entries are safe and will come back as soon as access is renewed.',
    accessInviteText: 'superposition is a private app.',
    accessInBot: 'access is arranged in the bot.',
    renewAccess: 'renew access',
    getAccess: 'get access',
    writeTo: (p: { handle: string | null }): string =>
      `message ${p.handle ? `@${p.handle}` : 'katya'} and send this number:`,
    telegramIdAria: 'your telegram id',
    writeKate: 'message katya',
    accessRetry: 'access is open — sign in',
    authFailedTitle: "couldn't sign in",
    tryAgain: 'try again',
  },

  save: {
    pending: 'unsaved changes',
    retry: 'retry',
    draft: 'draft',
    confirmReload:
      'load the server version? your unsaved local edits will be replaced. save the draft first if you need them.',
    reloadFailed: "couldn't load the data. your draft is kept in the open window — try again.",
    takeServer: 'use server version',
    draftTitle: 'unsaved draft',
    draftText:
      "the server didn't accept the changes. copy this text or download it as a file — nothing is lost while this window is open.",
    download: 'download file',
    conflict: 'the data changed on another device — save your draft before loading the server version',
  },

  errors: {
    noSession: 'no active session',
    timeout: "the server didn't respond in time",
    offline: 'no connection to the server',
    requestFailed: 'the request failed',
    unknown: 'something went wrong',
    offlineUnsaved: 'no connection to the server. changes not saved — copy or download the draft',
    timeoutUnsaved: "the server didn't respond in time. changes not saved",
    unauthorized: 'your telegram session has expired. close the mini app and open it again',
    noAccess: 'your access has expired. your entries are safe — everything comes back once it is renewed',
    conflict: 'the data changed on another device',
    tooLarge: 'the file is over 5 MiB — it was not applied or trimmed',
    server: (p: { requestId: string | null }): string =>
      `the service failed${p.requestId ? ` (request ${p.requestId})` : ''}. changes not saved`,
  },

  settings: {
    title: 'settings',
    name: 'name',
    namePlaceholder: 'name',
    genderLabel: 'how to address you',
    genderM: 'as a man',
    genderF: 'as a woman',
    genderN: "doesn't matter",
    remindersOn: 'ask me in the bot every day how the day went',
    remindAt: 'what time to ask',
    language: 'language',
    languageRu: 'русский',
    languageEn: 'english',
  },

  wheel: {
    title: 'balance wheel',
    titleOf: (p: { name: string; gender: 'm' | 'f' | 'n' | null }): string => `${possessive(p.name)} balance wheel`,
    aria: 'wheel of eight spheres',
    outOf10: (p: { value: number }): string => `${p.value} of 10`,
    vertexAria: (p: { sphere: string; value: number }): string => `${p.sphere}: ${p.value} of 10`,
    balanceIndex: 'balance index',
    inner: 'inner',
    outer: 'outer',
    dayFixed: 'day recorded ✓ — update',
    fixDay: 'record the day',
    note: "the balance index is the author's heuristic built on your own ratings. it doesn't measure coherence or predict income.",
  },

  matrix: {
    title: 'matrix',
    suggest: {
      sostoyanie: ['meditation', '10 minutes of silence', 'state journal', 'walk without phone'],
      zdorovie: ['2 l of water', 'workout', 'in bed by 11 pm', '10,000 steps'],
      otnosheniya: ['call someone close', 'time without phone', 'text a friend'],
      prostranstvo: ['clear one surface', '15-minute tidy-up', 'tidy the desk'],
      dengi: ['tally the day', 'one money step', 'send an invoice'],
      produkt: ['an hour on the product', 'one finished piece', 'show the result'],
      vliyanie: ['one post', 'a thought in public', 'go live'],
      realizatsiya: ['get it to "done"', 'close a task', 'clear unfinished stuff'],
    } as Record<SphereId, string[]>,
    goalAria: (p: { skill: string; done: number; goal: number | null; lost: boolean }): string =>
      `${p.skill}: ${p.done} this month${p.goal ? `, goal ${p.goal}${p.lost ? ', out of reach now' : ''}` : ', no goal set'}`,
    today: 'today',
    prevMonth: 'previous month',
    nextMonth: 'next month',
    goalHeadTotal: 'total',
    goalHeadGoal: 'goal',
    renameSphereAria: (p: { sphere: string }): string => `rename sphere ${p.sphere}`,
    addSkillAria: (p: { sphere: string }): string => `add a skill to ${p.sphere}`,
    addSkillEmpty: '+ add a skill',
    renameSkillAria: (p: { skill: string }): string => `rename skill ${p.skill}`,
    deleteSkillAria: (p: { skill: string }): string => `delete skill ${p.skill}`,
    skillsPerDay: 'skills per day',
    balancePerDay: 'balance index, %',
    note: 'the balance index comes from the wheel on the day you record it.',
    newSkill: (p: { sphere: string }): string => `new skill · ${p.sphere}`,
    skillNameLabel: 'skill name',
    skillPlaceholder: 'your own step…',
    add: 'add',
    goalTitle: (p: { skill: string }): string => `monthly goal: ${p.skill}`,
    goalQuestion: 'how many times a month do you want to do this?',
    goalInputLabel: 'times a month',
    goalNote: "lean on last month: keep the skill, or change it if it doesn't lead to the goal.",
    removeGoal: 'remove goal',
    renameSkill: 'rename skill',
    renameSphere: 'rename sphere',
    newNameLabel: 'new name',
    renameSkillNote: "the skill's marks will be kept.",
    renameSphereNote: (p: { standard: string }): string =>
      `ratings, skills and links to the book are kept. the standard name is "${p.standard}".`,
    restoreName: (p: { standard: string }): string => `restore "${p.standard}"`,
    deleteSkill: 'delete skill',
    deleteTitle: (p: { skill: string }): string => `delete skill "${p.skill}"?`,
    deleteText: (p: { marks: number }): string =>
      `its ${plural(p.marks, 'mark', 'marks')} will go with it. you won't be able to get them back.`,
    delete: 'delete',
  },

  observer: {
    title: 'the observer',
    looking: 'observing…',
    lookLine1: 'time',
    lookLine2: 'to observe',
    hint: 'tap the dial',
    persona: 'today i observe you as',
    oneStep: 'one step',
    whyInBook: 'why — in the book:',
    lakePractice: (p: { sphere: string }): string => `lake practice: ${p.sphere} →`,
    emptyLine1: 'the observer looks into the matrix and the wheel and collapses the superposition into one honest conclusion.',
    emptyLine2: "tap the dial when you're ready to hear it.",
    past: (p: { count: number }): string => `past observations — ${p.count}`,
    showMore: 'show more',
    note: "the observer runs on transparent rules over your own marks and ratings. it doesn't go online and sends nothing out.",
  },

  reports: {
    title: 'monthly reports',
    verdict: {
      keep: 'keep',
      replace: 'replace skills',
      neglected: 'neglected',
      grows: 'grows on its own',
      steady: 'steady',
      unknown: '—',
    },
    month: (p: { n: number }): string => `month ${p.n}`,
    period: (p: { from: DayKey; to: DayKey; filled: number; days: number }): string =>
      `${formatDay(p.from, 'en')} — ${formatDay(p.to, 'en')} · ${p.filled} of ${plural(p.days, 'day', 'days')}`,
    colSphere: 'sphere',
    colSkills: 'skills',
    colValue: 'rating',
    colVerdict: 'verdict',
    note: (p: { spheres: number }): string =>
      `skills: the share of 1s out of the possible for the month. rating: satisfaction with the sphere from the first recorded day to the last. ${plural(p.spheres, 'sphere', 'spheres')}, whole period.`,
  },

  lake: {
    title: 'the fairytale lake',
    intro: 'choose the sphere you are charging and sink beneath the water. spheres with a recording play a meditation; the rest play a tone to dive into.',
    intentionAria: 'intention',
    recorded: (p: { minutes: number }): string => `recording · ${p.minutes} min`,
    soon: 'soon',
    durationAria: 'duration',
    minutes: (p: { minutes: number }): string => `${p.minutes} min`,
    enter: 'enter the lake',
    practiceNote: (p: { title: string; minutes: number }): string =>
      `meditation "${p.title}", ${p.minutes} min. best with headphones and eyes closed. the recording keeps playing with the screen off.`,
    soundOff: 'sound off',
    soundOn: 'sound on',
    toneNote:
      'a meditation for this sphere is coming soon. for now, dive in with a tone: best with headphones, two tones at 180 and 188 hz, 8 hz apart. breathing and the timer work without sound too.',
    weekly: 'new practices every week',
    mediaTitle: (p: { title: string }): string => `the fairytale lake: ${p.title}`,
    // название записи как в книге
    practiceTitle: (p: { title: string }): string => p.title,
    pause: 'pause',
    meditation: (p: { title: string }): string => `meditation: ${p.title}`,
    intention: (p: { sphere: string }): string => `intention: ${p.sphere}`,
    resume: 'resume practice',
    recordFailed: 'the recording didn\'t start. tap "resume" again or check your connection.',
    leave: 'back to shore',
    stopSound: 'turn sound off',
    startingSound: 'turning sound on…',
    startSound: 'turn sound on',
    soundFailed: (p: { message: string }): string =>
      `sound didn't start: ${p.message}. the practice goes on — the timer and breathing still work.`,
    noStereo: "stereo panning isn't available on this device — the tones play together, so this isn't binaural mode.",
    breath: {
      in: 'breathe in',
      out: 'breathe out',
    },
    sound: {
      superseded: 'cancelled by a newer start',
      unavailable: 'sound is unavailable on this device',
      noWebAudio: "this browser doesn't support web audio",
    },
  },

  method: {
    title: 'the method',
    mediaTitle: (p: { title: string }): string => `meditation: ${p.title}`,
    pauseAria: (p: { title: string }): string => `pause: ${p.title}`,
    listenAria: (p: { title: string }): string => `play meditation: ${p.title}`,
    practice: (p: { title: string }): string => `meditation · ${p.title}`,
    seekAria: (p: { title: string }): string => `seek: ${p.title}`,
    playFailed: "couldn't start, tap again",
    allChapters: '← all chapters',
    chapter: 'chapter',
    openingChapter: 'opening the chapter…',
    intro: 'dear idiots, the book the wheel, the matrix and the observer grew out of. read it chapter by chapter, starting anywhere.',
    loadingIndex: 'loading contents…',
  },
};
